import React, { useState } from 'react';
import { connect } from 'react-redux';
import { login } from '@/services/users';
import { CustomLink } from '@/components/navigation';
import { Trans } from '@lingui/react/macro';
import { ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { loadUser } from '@/state/global/creators';
import { Dispatch } from 'redux';
import { IState } from '@/state/IState';
import { TranslatableText } from '@/types';
import UserPageInput from './UserPageInput';
import theme from './LoginForm.module.scss';
import { HttpStatusCode } from '@/HttpStatusCodes';
import ExternalLink from '@/components/navigation/ExternalLink';
import { Icon } from '@/components/Icons';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { Checkbox, Label, Button as RubusButton } from '@ygtechnologies/rubus-ui';
import { useCurrentCulture } from '@/utils/hooks';

const mapStateToProps = (state: IState) => ({});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    loadUser: () => loadUser(dispatch),
});

type OwnProps = ReturnType<typeof mapDispatchToProps> & {
    initialEmail?: string;
    inviteToken?: string;
    onLogin?: () => void;
};

const LoginForm: React.FC<OwnProps> = ({ inviteToken, loadUser, onLogin, initialEmail = '' }) => {
    const [cultureInfo] = useCurrentCulture();
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(false);
    const [serverErrors, setServerErrors] = useState<TranslatableText[]>([]);
    const [saving, setSaving] = useState(false);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        setSaving(true);
        setServerErrors([]);

        try {
            await login(email, password, remember, inviteToken);
            await loadUser();

            setSaving(false);

            onLogin && onLogin();
        } catch (e: any) {
            setSaving(false);

            if (e.status === HttpStatusCode.Unauthorized) {
                setServerErrors([<Trans>Invalid username or password</Trans>]);
            } else {
                throw e;
            }
        }
    };

    return (
        <ValidationForm className={theme.container} onSubmit={handleFormSubmit}>
            <Icon fontScale={false} name="logo-big" fill="#ff4843" className={theme.logo} />
            <h2>
                <Trans>Log in</Trans>
            </h2>

            <span className={theme.infoText}>
                <Trans>Email and password</Trans>
            </span>
            <UserPageInput
                className={theme.inputField}
                required
                label={<Trans>Email</Trans>}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
            />
            <UserPageInput
                className={theme.inputField}
                label={<Trans>Password</Trans>}
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <Checkbox
                className={theme.checkbox}
                name="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}>
                <div className={theme.rememberMe}>
                    <Label variant="secondary">
                        <Trans>Remember me</Trans>
                    </Label>
                    <Label className={theme.underText} variant="secondary">
                        <Trans>Save my login details for next time.</Trans>
                    </Label>
                </div>
            </Checkbox>
            {serverErrors.length > 0 && <ValidationSummary externalErrors={serverErrors} />}
            <RubusButton
                size="lg"
                className={theme.loginButton}
                icon={faArrowRight}
                iconPosition="right"
                type="submit"
                variant="primary"
                loading={saving}
                block>
                <Trans>Log in</Trans>
            </RubusButton>
            <CustomLink to={`/forgot-password?hs_lang=${cultureInfo?.languageCode || ''}`} className={theme.textLink}>
                <Trans>Forgot password?</Trans>
            </CustomLink>
            <ExternalLink href="https://www.rucoria.com" className={theme.textLink}>
                <Trans>Find out more about Rucoria?</Trans>
            </ExternalLink>
        </ValidationForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
